/**
 * Playable greybox host: input → 20 Hz sim → 60 Hz render + transform juice.
 * Entry for Vite dev/build (`pnpm --filter shell-web dev`).
 *
 * Levels: GDD-SHARED ad beat sheet 1–5 (data from @afr/content-schema).
 * Select via query: `?level=1` … `?level=5` or `?level=level-05` (default L5).
 *
 * W8 juice: count popups, hit flash, screen shake, WebAudio, readable fail,
 * one-tap restart.
 */

import { GreyboxRenderer } from "@afr/render-three";
import type { RenderSnapshot } from "@afr/render-three";
import type { LevelDef, SimSnapshot } from "@afr/sim";
import {
  bindJuiceAudioGestureUnlock,
  createJuiceAudio,
} from "./audio/JuiceAudio.js";
import { createContinueHud } from "./hud/ContinueHud.js";
import { createFailHud } from "./hud/FailHud.js";
import { createUnitCountHud } from "./hud/UnitCountHud.js";
import { LateralInput } from "./input/LateralInput.js";
import {
  AD_BEAT_LEVELS,
  C05_PACING_BY_ID,
  getPacingOverlay,
  resolveLevelFromSearch,
} from "./levels/adBeatLevels.js";
import { breakevenPairsForLevel } from "./levels/breakevenPairs.js";
import { RunLoop } from "./loop/RunLoop.js";

function toRenderSnap(
  snap: SimSnapshot,
  triggeredGateIds: readonly string[],
): RenderSnapshot {
  return {
    pathZ: snap.pathZ,
    lateralX: snap.lateralX,
    pathLength: snap.pathLength,
    n: snap.n,
    formation: snap.formation,
    inMecha: snap.inMecha,
    mecha: snap.mecha
      ? { hp: snap.mecha.hp, maxHp: snap.mecha.maxHp }
      : null,
    tick: snap.tick,
    time: snap.time,
    phase: snap.phase,
    friendlyDps: snap.friendlyDps,
    activeWaveId: snap.activeWaveId,
    activeWaveHp: snap.activeWaveHp,
    activeWaveMaxHp: snap.activeWaveMaxHp,
    triggeredGateIds,
  };
}

function applyLevelMarkers(
  renderer: GreyboxRenderer,
  level: LevelDef,
): void {
  const climax = level.climaxWave;
  // Pass sim wave HP so the render horde can scale zombie density (pressure wall).
  const waves = [...(level.waves ?? []), ...(climax ? [climax] : [])].map(
    (w) => ({
      id: w.id,
      z: w.z,
      contactHalfLength: w.contactHalfLength,
      hp: w.hp,
    }),
  );
  renderer.setLevelMarkers({
    gates: level.gates,
    waves,
    pedestal: level.pedestal ?? null,
    pathHalfWidth: 3.5,
  });
}

function mountLevelPicker(
  parent: HTMLElement,
  current: LevelDef,
  onPick: (level: LevelDef) => void,
): void {
  const bar = document.createElement("div");
  bar.setAttribute("data-testid", "level-picker");
  Object.assign(bar.style, {
    position: "absolute",
    top: "8px",
    left: "8px",
    right: "8px",
    zIndex: "20",
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    fontFamily: "system-ui, sans-serif",
    pointerEvents: "auto",
  } satisfies Partial<CSSStyleDeclaration>);

  for (const level of AD_BEAT_LEVELS) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.textContent = level.id.replace("level-0", "L");
    btn.title = level.name ?? level.id;
    btn.setAttribute("data-level-id", level.id);
    const active = level.id === current.id;
    Object.assign(btn.style, {
      border: active ? "1px solid #fbbf24" : "1px solid #4b5563",
      background: active ? "#78350f" : "#1f2937",
      color: "#f9fafb",
      borderRadius: "6px",
      padding: "4px 10px",
      fontSize: "12px",
      cursor: "pointer",
    } satisfies Partial<CSSStyleDeclaration>);
    btn.addEventListener("click", () => onPick(level));
    bar.appendChild(btn);
  }

  const label = document.createElement("span");
  label.setAttribute("data-testid", "level-label");
  label.textContent = current.name ?? current.id;
  Object.assign(label.style, {
    marginLeft: "8px",
    color: "#d1d5db",
    fontSize: "12px",
    alignSelf: "center",
  } satisfies Partial<CSSStyleDeclaration>);
  bar.appendChild(label);

  parent.appendChild(bar);
}

function mount(): void {
  const root = document.getElementById("app");
  if (!root) {
    throw new Error("#app missing");
  }

  Object.assign(root.style, {
    position: "relative",
    width: "100%",
    height: "100%",
    margin: "0",
    overflow: "hidden",
    background: "#111827",
  } satisfies Partial<CSSStyleDeclaration>);

  const canvas = document.createElement("canvas");
  canvas.setAttribute("data-testid", "game-canvas");
  Object.assign(canvas.style, {
    display: "block",
    width: "100%",
    height: "100%",
    touchAction: "none",
  } satisfies Partial<CSSStyleDeclaration>);
  root.appendChild(canvas);

  let level = resolveLevelFromSearch(window.location.search);

  const hud = createUnitCountHud({ parent: root });
  const input = new LateralInput({ target: canvas });
  const loop = new RunLoop({
    level,
    autoStart: true,
    seed: 1,
    maxContinues: 1,
  });

  // Procedural WebAudio (AudioContext) — unlock on first gesture (iOS)
  const juiceAudio = createJuiceAudio();
  const unbindAudioGesture = bindJuiceAudioGestureUnlock(juiceAudio);

  const renderer = new GreyboxRenderer(canvas, {
    pathHalfWidth: 3.5,
  });
  applyLevelMarkers(renderer, level);

  /**
   * One-tap restart: fresh sim run via existing SIM_API (createRun path),
   * no page reload. Identifier `restart` is load-bearing for the verifier.
   */
  const restart = (): void => {
    failHud.hide();
    continueHud.hide();
    loop.reset({
      level,
      autoStart: true,
      seed: 1,
      maxContinues: 1,
    });
    applyLevelMarkers(renderer, level);
    const snap = loop.getSimSnapshot();
    renderer.snapToSnapshot(toRenderSnap(snap, loop.getTriggeredGateIds()));
    hud.update(snap);
    lastPhase = snap.phase;
    lastFailReason = null;
  };

  const continueHud = createContinueHud({
    parent: root,
    onAccept: () => {
      if (loop.acceptContinue()) {
        continueHud.hide();
        failHud.hide();
        juiceAudio.unlock();
      }
    },
    onDecline: () => {
      loop.declineContinue();
      continueHud.hide();
      // Show readable fail + one-tap restart (not a silent freeze)
      const snap = loop.getSimSnapshot();
      failHud.show({
        reason: "results_lose",
        continuesUsed: snap.continuesUsed,
        maxContinues: loop.maxContinues,
      });
    },
  });

  const failHud = createFailHud({
    parent: root,
    desaturateTarget: canvas,
    onRestart: () => {
      juiceAudio.unlock();
      restart();
    },
    onContinue: () => {
      if (loop.acceptContinue()) {
        failHud.hide();
        continueHud.hide();
        juiceAudio.unlock();
      }
    },
  });

  const debug = window as unknown as {
    __afrRunLoop?: RunLoop;
    __afrLevel?: LevelDef;
    __afrLevels?: readonly LevelDef[];
    /** Level-keyed breakeven teaching pairs (from level data config). */
    __afrBreakevenPairs?: ReturnType<typeof breakevenPairsForLevel>;
    /** C05 pacing overlay for the active level (45–90s + six beats). */
    __afrPacing?: ReturnType<typeof getPacingOverlay>;
    __afrPacingById?: typeof C05_PACING_BY_ID;
    __afrReady?: boolean;
    __afrRestart?: () => void;
  };
  debug.__afrRestart = restart;
  debug.__afrPacingById = C05_PACING_BY_ID;

  const loadLevel = (next: LevelDef): void => {
    level = next;
    failHud.hide();
    continueHud.hide();
    loop.reset({
      level,
      autoStart: true,
      seed: 1,
      maxContinues: 1,
    });
    applyLevelMarkers(renderer, level);
    // Refresh picker highlight via URL (no full reload)
    const url = new URL(window.location.href);
    url.searchParams.set("level", level.id);
    window.history.replaceState({}, "", url.toString());
    // Rebuild picker labels
    const old = root.querySelector("[data-testid=level-picker]");
    old?.remove();
    mountLevelPicker(root, level, loadLevel);
    const snap = loop.getSimSnapshot();
    renderer.snapToSnapshot(toRenderSnap(snap, loop.getTriggeredGateIds()));
    hud.update(snap);
    lastPhase = snap.phase;
    lastFailReason = null;
    debug.__afrLevel = level;
    debug.__afrBreakevenPairs = breakevenPairsForLevel(level.id);
    debug.__afrPacing = getPacingOverlay(level.id);
    root.setAttribute("data-level-id", level.id);
  };

  mountLevelPicker(root, level, loadLevel);

  const resize = (): void => {
    const w = root.clientWidth || window.innerWidth;
    const h = root.clientHeight || window.innerHeight;
    renderer.setSize(w, h);
  };
  resize();
  window.addEventListener("resize", resize);

  const initial = loop.getSimSnapshot();
  renderer.snapToSnapshot(
    toRenderSnap(initial, loop.getTriggeredGateIds()),
  );
  hud.update(initial);

  let last = performance.now();
  let raf = 0;
  let lastPhase = initial.phase;
  let lastFailReason: "count_zero" | "mecha_dead" | null = null;

  const frame = (now: number): void => {
    raf = requestAnimationFrame(frame);
    const dt = Math.min(0.1, (now - last) / 1000);
    last = now;

    // Pause sim while fail overlay is up (readable fail, not silent freeze)
    if (failHud.isVisible()) {
      renderer.render();
      return;
    }

    loop.setLateral(input.getLateral());
    const result = loop.step(dt);

    for (const e of result.events) {
      if (e.type === "transform") {
        const pedZ = level.pedestal?.z ?? result.simSnap.pathZ;
        renderer.notifyTransform(result.simSnap.lateralX, pedZ);
        // Absorb count popup (beyond gate popups) — feel the consume-N
        renderer.notifyCountDelta({
          delta: -Math.max(1, e.nPre),
          kind: "absorb",
          x: result.simSnap.lateralX,
          z: pedZ,
          text: `−${e.nPre}`,
        });
        juiceAudio.play("transform");
      }
      if (e.type === "gate_applied") {
        juiceAudio.play("gate");
        // Light shake on gate pass (gateBreak already pops op text)
        renderer.triggerShake(0.12);
      }
      if (e.type === "n_changed" && e.reason === "combat") {
        const delta = e.nAfter - e.nBefore;
        if (delta < 0) {
          juiceAudio.play("loss");
          renderer.notifyCountDelta({
            delta,
            kind: "loss",
            x: result.simSnap.lateralX,
            z: result.simSnap.pathZ,
          });
          renderer.pulseHitFlash(Math.min(1, 0.5 + Math.min(0.5, -delta / 10)));
          renderer.triggerShake(Math.min(0.4, 0.14 + -delta * 0.035));
        }
      }
      if (e.type === "wave_contact") {
        renderer.pulseHitFlash(0.65);
        renderer.triggerShake(0.22);
      }
      if (e.type === "fail") {
        lastFailReason = e.reason;
        juiceAudio.play("fail");
        renderer.triggerShake(0.55);
      }
    }

    const renderSnap = toRenderSnap(
      result.renderSnap,
      loop.getTriggeredGateIds(),
    );
    renderer.applySnapshot(renderSnap, result.frameDt);

    // Explosive canisters: apply detonation damage through SIM aggregate wave HP
    // (same Model B pool squad contact combat uses). Horde death juice reacts
    // to the HP drop via activeWaveHp (w3 death juice).
    const detonations = renderer.drainCanisterDetonations();
    let hudSnap = result.simSnap;
    if (detonations.length > 0) {
      for (const d of detonations) {
        loop.applyPropDamage(d.damage, d.waveId);
      }
      juiceAudio.play("explosion");
      // Re-apply snapshot so horde density / death juice see reduced HP immediately
      hudSnap = loop.getSimSnapshot();
      renderer.applySnapshot(
        toRenderSnap(hudSnap, loop.getTriggeredGateIds()),
        0,
      );
    }

    renderer.render();
    hud.update(hudSnap);

    const phase = result.simSnap.phase;
    if (phase !== lastPhase) {
      if (phase === "continue_offer") {
        // Readable fail treatment first; continue is optional on the same screen
        failHud.show({
          reason: lastFailReason ?? "continue_offer",
          continuesUsed: result.simSnap.continuesUsed,
          maxContinues: loop.maxContinues,
        });
        continueHud.hide();
      } else if (phase === "results_lose") {
        failHud.show({
          reason: lastFailReason ?? "results_lose",
          continuesUsed: result.simSnap.continuesUsed,
          maxContinues: loop.maxContinues,
        });
        continueHud.hide();
      } else if (
        phase === "results_win" ||
        phase === "running" ||
        phase === "climax" ||
        phase === "mecha"
      ) {
        failHud.hide();
        continueHud.hide();
      }
      lastPhase = phase;
    }
  };
  raf = requestAnimationFrame(frame);

  debug.__afrRunLoop = loop;
  debug.__afrLevel = level;
  debug.__afrLevels = AD_BEAT_LEVELS;
  debug.__afrBreakevenPairs = breakevenPairsForLevel(level.id);
  debug.__afrPacing = getPacingOverlay(level.id);
  debug.__afrReady = true;
  root.setAttribute("data-ready", "true");
  root.setAttribute("data-level-id", level.id);

  window.addEventListener(
    "beforeunload",
    () => {
      cancelAnimationFrame(raf);
      unbindAudioGesture();
      juiceAudio.dispose();
      input.dispose();
      hud.dispose();
      continueHud.dispose();
      failHud.dispose();
      renderer.dispose();
    },
    { once: true },
  );
}

mount();
